<template>
  <div v-if="lead && form" class="modal-grid">
    <div class="modal-card">
      <div class="form-group">
        <label class="form-label mb-0">{{ $t('status') }}</label>
        <div class="font-weight-bold"
             v-if="statusObject"
             :style="`color: ${statusObject.color}`">
          {{ statusObject.translatedTitle }}
        </div>
      </div>
      <div>
        <label class="form-label mb-0">{{ $t('responsible') }}</label>
        <div>
          <OneFieldEdit modelName="staff"
                        v-if="initiated"
                        :multiple="true"
                        @input="responsibleChanged"
                        v-model="form.multiple_responsible"/>
        </div>
      </div>
    </div>

    <div class="modal-card">
      <div class="row">
        <div class="col-md-4 col-12">

          <div class="form-group">
            <label class="form-label mb-0">{{ $t('creation_date') }}</label>
            <div>{{ formatDateTime(lead.created_at) }}</div>
          </div>

          <div class="form-group">
            <label class="form-label mb-0">{{ $t('who_created') }}</label>
            <div>{{ lead.author ? lead.author.full_name : '-' }}</div>
          </div>
        </div>

        <div class="col-md-4 col-12">

          <div class="form-group">
            <label class="form-label mb-0">{{ $t('updated_date') }}</label>
            <div>{{ formatDateTime(lead.updated_at) }}</div>
          </div>

          <div class="form-group">
            <label class="form-label mb-0">{{ $t('who_updated') }}</label>
            <div>{{ lead.updater ? lead.updater.full_name : '-' }}</div>
          </div>

        </div>
        <div class="col-md-4 col-12">

          <div class="form-group"
               v-if="lead.source">
            <label class="form-label mb-0">{{ $t('lead_channel') }}</label>
            <div>{{ $t(lead.source === 'call' ? 'call_noun' : lead.source) }}</div>
          </div>

          <div class="form-group">
            <label class="form-label mb-0">{{ $t('source') }}</label>
            <div v-if="clientSources.length">
              <SelectGroup :options="clientSources"
                           class="mb-0 sm w-100"
                           :nullOption="true"
                           :showlabel="false"
                           :required="false"
                           @input="sourceChanged"
                           :label="$t('source')"
                           variant="white"
                           v-model="form.source_id"/>
            </div>
          </div>

          <div class="form-group" v-if="lead.utm">
            <label class="form-label mb-0">UTM</label>
            <div class="" style="word-break: break-all">{{ lead.utm }}</div>
          </div>

        </div>
      </div>
    </div>

    <div class="modal-card overflow-scroll nowrap modal-grid-item-w-100">
      <div class="d-flex flex-nowrap">
        <button v-for="l in CRMStages"
                class="btn-themed btn-themed-outline btn-themed-squared mr-2 lead-status-btn"
                :style="`border-color: ${l.color}; color: ${l.color};`"
                @click="changeStatus(l)"
                :key="`le-${l.id}`">
          {{ l.translatedTitle }}
        </button>
      </div>
    </div>

    <div class="modal-card">
      <div class="modal-card-title">{{ $t('contact_person') }}</div>
      <div>
        <div class="row align-items-center">
          <div class="col col-auto">
            <div v-if="!titleEditing">
              {{ lead.patient_name }}
              <span v-if="lead.urgent"
                    class="notification-bubble position-relative d-inline-block"
                    style="top: 0; left: 10px"></span>
            </div>
            <div class="row">
              <div class="col">
                <InputGroup class="mb-0 sm w-100"
                            v-if="titleEditing"
                            :placeholder="$t('name')"
                            @input="changed = true"
                            v-model="form.fname"/>
              </div>
              <div class="col">
                <InputGroup class="mb-0 sm w-100"
                            v-if="titleEditing"
                            :placeholder="$t('surname')"
                            @input="changed = true"
                            v-model="form.lname"/>
              </div>
            </div>
          </div>
          <div class="col col-auto">
            <a class="pointer"
               @click="titleEditingClick">
              <CloseSvg class="svg-light svg-size-sm" v-if="titleEditing"/>
              <EditSvg v-if="!titleEditing"/>
            </a>
            <a v-if="titleEditing"
               class="pointer px-3"
               @click="saveChange">
              <TickSvg class="svg-light svg-size-sm"/>
            </a>
          </div>
        </div>

        <div style="max-height: 24px;">
          <OutgoingCallElement :phone="lead.phone" color="black"/>
          <a class="ml-3"
             :href="`tel:${lead.phone}`"
             v-if="!rights.includes('hide_patient_phone')"
             v-b-tooltip.hover
             :title="$t('call')">
            <PhoneSvg class="svg-primary"/>
          </a>
          <WhatsappTelegramIcon :phone="lead.phone"
                                :patient-id="form.patient_id"/>
        </div>
        <div v-if="lead.email">{{ lead.email }}</div>
        <div v-if="callVendor || recordLink">
          <a class="mt-2 pointer text-underlined"
             @click="listen">
            {{ $t('listen') }}
          </a>
        </div>
        <ValidationError validationId="phone" class="mt-3"/>
      </div>

      <div v-if="possiblePatients.length" class="form-group">
        <label class="form-label mb-0">{{ $t('by_phone_found', { phone: lead.phone}) }}:</label>
        <div>
          <a class="d-block text-underlined pointer"
             v-for="p in possiblePatients"
             @click="lead.patient = p; form.patient_id = p.id; changed = true"
             :key="`pat-p-${p.id}`">{{ p.full_name }} ({{ p.phone }})
          </a>
        </div>
      </div>

      <div>
        <div class="row align-items-end">
          <div class="col-6">
            <ServiceMultiSelect :label="$t('patient')"
                                :dblclick="false"
                                model="patient"
                                :multiple="false"
                                :readonly="['recorded'].includes(lead.status)"
                                title="full_name"
                                class="mb-0 sm"
                                @input="patientSelected"
                                :dbsearch="true"
                                v-model="lead.patient"/>
          </div>
          <div class="col-6">
            <button class="btn-themed btn-success btn-themed-squared"
                    v-if="!['recorded'].includes(lead.status)"
                    @click="recordPatient">
              {{ $t('record_on_visit') }}
            </button>
          </div>
        </div>

        <div v-if="lead.patient" class="mt-1 small">
          <router-link :to="{ name: 'PatientVisits', params: { id: lead.patient.id } }">
            {{ $t('patient_card') }}
          </router-link>
        </div>
      </div>
    </div>

    <div class="modal-card">
      <div class="modal-card-title">
        {{ $t('recorded_on_visit') }}: {{ lead.record ? lead.record.readableStart : '-' }}
      </div>
      <DateGroup :label="$t('recall')"
                 :readonly="!!form.call_date"
                 @input="changed = true"
                 :clearable="true"
                 v-if="form.call_date"
                 v-model="form.call_date"/>
      <div>
        <AutosizeTextArea v-model="form.reason"
                          @input="changed = true"
                          :label="$t('application_reason')"/>
      </div>
      <div>
        <AutosizeTextArea v-model="form.comment"
                          @input="changed = true"
                          :label="$t('client_comment')"/>
      </div>
    </div>

    <div class="modal-card">
      <div class="row mb-3 row-narrow">
        <div class="col">
          <button class="btn btn-themed btn-themed-outline btn-themed-squared w-100"
                  @click="addRecallTask">
            {{ $t('recall') }}
          </button>
        </div>
        <div class="col">
          <button class="btn btn-themed btn-themed-outline btn-themed-squared w-100 px-0"
                  @click="addTask">
            {{ $t('add_task') }}
          </button>
        </div>
      </div>
      <div class="mt-3 mb-3 task-scrollable-modal-part" v-if="lead.allTasks && lead.allTasks.length">
        <TaskTeaser v-for="(task, tInd) in lead.allTasks"
                    :show-info="false"
                    @loadTasks="parse"
                    :key="`task-${task.id}-${tInd}`"
                    :task="task"/>
      </div>
    </div>

    <div class="modal-card overflow-auto">

      <b-tabs nav-class="sub-menu"
              content-class="pt-4"
              class="w-100">
        <b-tab active :title="$t('comments')">

          <GeneralCommentsBlock :object="lead"
                                :black-label="false"
                                model="lead_comment"
                                parent-column="lead_id"/>
        </b-tab>
        <b-tab :title="$t('live_feed')">
          <PatientsCommentsList v-if="lead.patient"
                                :patient="lead.patient"
                                :show-checks="true"
                                @checksChanged="patientCommentTypeChecked = $event"
                                :checked="patientCommentTypeChecked"
                                :show-images="false"/>
        </b-tab>
        <b-tab :title="$t('messenger')"
               lazy>
          <CRMMessengerBlock :phone="lead.phone"
                             :patientId="lead.patient_id"/>
        </b-tab>
      </b-tabs>
    </div>

    <div class="modal-card modal-footer-buttons no-print text-right modal-grid-item-w-100">

      <button class="btn-themed align-middle btn-themed-squared gray mr-md-3 mr-2"
              v-if="rights.includes('crmtask_delete')"
              @click="deleteLead">
        {{ $t('delete') }}
      </button>

      <b-button class="cancel mr-md-3 mr-2 px-3 d-inline-block align-middle w-auto btn-themed-squared btn-themed-outline"
                variant="themed"
                @click="edit">
        {{ $t('edit') }}
      </b-button>

      <button :disabled="loading"
              @click="finished"
              class="btn-themed align-middle btn-themed-squared">
        {{ $t('save') }}
      </button>
    </div>
  </div>
</template>

<script setup>
import TaskTeaser from "@/components/parts/header/TaskTeaser.vue"
import ValidationError from "@/components/form/ValidationError.vue"
import SelectGroup from "@/components/form/SelectGroup.vue"
import DateGroup from "@/components/form/DateGroup.vue"
import AutosizeTextArea from "@/components/form/AutosizeTextArea.vue"
import GeneralCommentsBlock from "@/components/parts/general/GeneralCommentsBlock.vue"
import CRMMessengerBlock from "@/components/parts/crm/CRMMessengerBlock.vue"
import EditSvg from "@/assets/svg-vue/general/edit.svg.vue"
import ServiceMultiSelect from "@/components/form/ServiceMultiSelect.vue"
import TickSvg from "@/assets/svg-vue/form/tick.svg.vue"
import WhatsappTelegramIcon from "@/components/pages/patients/WhatsappTelegramIcon.vue"
import InputGroup from "@/components/form/InputGroup.vue"
import CloseSvg from "@/assets/svg-vue/general/close.svg.vue"
import OutgoingCallElement from "@/components/parts/patients/OutgoingCallElement.vue"
import { useStore } from "vuex"
import { formatDateTime } from "@/extensions/filters/filters"
import { BinotelService } from "@/services/api/binotel.api.service"
import { EloquentService } from "@/services/api/eloquent.api.service"
import { PatientService } from "@/services/api/patient.api.service"
import moment from "moment/moment"
import { computed, getCurrentInstance, nextTick, onBeforeMount, onBeforeUnmount, onMounted, ref, watch} from "vue"
import { appendFormdata, copyObject} from "@/extensions/prototypes/prototypes"
import { useRoute, useRouter } from "vue-router"
import OneFieldEdit from "@/components/parts/general/OneFieldEdit.vue"
import PhoneSvg from "@/assets/svg-vue/header/phone_t.svg.vue"
import {patientCommentTypes} from "@/dictionaries/dictionary"
import PatientsCommentsList from "@/components/parts/patients/comment/PatientsCommentsList.vue"

const store = useStore()
const route = useRoute()
const router = useRouter()

const form = ref(null)
const initiated = ref(false)
const changed = ref(false)
const possiblePatients = ref([])
const responsibleEditing = ref(false)
const titleEditing = ref(false)
const patientCommentTypeChecked = ref([])
const emit = defineEmits(['needRefresh'])

const loading = computed(() => store.state.dom.loading)
const rightPaneOpened = computed(() => store.state.dom.rightPaneOpened)
const lead = computed(() => store.state.crm.lead)
const clientSources = computed(() => store.state.auth.clientSources)
const user = computed(() => store.state.auth.user)
const counter = computed(() => store.state.datatable.dataTableCounter)
const CRMStages = computed(() => store.state.auth.CRMStages)
const rights = computed(() => store.state.auth.rights)
const recordLink = computed(() => {
  if(lead.value?.record_link) return lead.value?.record_link
  return lead.value?.patient_comment?.variables?.record_link || null
})
const callVendor = computed(() => lead.value?.patient_comment?.variables?.vendor || null)
const statusObject = computed(() => CRMStages.value.find(status => status.id === form.value?.status_id) || null)

let bvModal = null
let noty = null
let i18n = null

onBeforeMount(() => {
  i18n = (getCurrentInstance())?.ctx?.$i18n
  if(!lead.value) {
    store.commit('setLead', copyObject({ id: route.params.id }))
  }
})

onMounted(async () => {
  const instance = (getCurrentInstance())
  bvModal = instance?.ctx?._bv__modal
  noty = instance?.appContext?.app?.noty
  patientCommentTypeChecked.value = patientCommentTypes.map(x => x.id)
  await parse()
  initiated.value = true
})

const edit = async () => {
  bvModal.hide('lead-show-modal')
  const newObject = JSON.parse(JSON.stringify(lead.value))
  store.commit('setModule', 'crm')
  store.commit('setSelectedModel', newObject)
  store.commit('setViewModel', 'lead')
  await nextTick()
  store.commit('setShowModal', true)
}

const saveChange = async () => {
  store.commit('cleanValidationErrors')
  try {
    let formData = new FormData()
    const formTmp = copyObject(form.value)
    if(formTmp.source_id && !formTmp.source) {
      formTmp.source = clientSources.value.find(obj => Number(obj.id) === Number(formTmp.source_id)).title
    }
    formTmp.multiple_responsible = formTmp.multiple_responsible.map(obj => obj.id)
    formData = appendFormdata(formData, formTmp)
    await EloquentService.update('lead', lead.value.id, formData)
    changed.value = false
    responsibleEditing.value = false
    titleEditing.value = false
    store.commit('incrementDataTableCounter')
  } catch (e) {
    noty.error(e?.response?.data?.message || i18n.t('error'))
  }
}

const parse = async () => {
  if(!lead.value) return
  possiblePatients.value = []
  if(!clientSources.value.length) {
    store.dispatch('getClientSources').then()
  }
  if(!CRMStages.value.length) {
    store.dispatch('getCRMStages').then()
  }
  const res = await EloquentService.show('lead', lead.value?.id)
  const data = res?.data || null
  store.commit('setLead', copyObject(data))
  emit('needRefresh')
  form.value = {
    reason: data.reason,
    comment: data.comment,
    manual_title: data.manual_title,
    source: data.source || null,
    source_id: data.source_id,
    fname: data.fname,
    lname: data.lname,
    phone: data.phone,
    patient_id: data.patient_id,
    call_date: data.call_date,
    status_id: data.status_id,
    multiple_responsible: data.multiple_responsible.length ? copyObject(data.multiple_responsible) : (data.responsible ? [data.responsible] : []),
  }
  if(!lead.value.patient) {
    possiblePatients.value = res.data?.possiblePatients || []
    if(possiblePatients.value.length === 1) {
      lead.value.patient = possiblePatients.value[0]
    }
  }
}

const changeStatus = async(status) => {
  if(status.id === 4 && form.value.patient_id) { // id = 4 is recorded
    const res = await PatientService.patientHasFutureRecords(form.value.patient_id)
    if(!res.data) {
      const res = confirm(i18n.t('patient_has_no_future_visits_continue'))
      if(!res) return;
    }
  }
  form.value.status_id = status.id
  lead.value.status_id = status.id
  await saveChange()
}

const sourceChanged = async () => {
  await nextTick()
  await saveChange()
  lead.value.source_id = form.value.source_id
}

const recordPatient = async () => {
  if(lead.value.patient && Number(lead.value.patient.id) !== Number(form.value.patient_id)) {
    form.value.patient_id = lead.value.patient.id
    await saveChange()
  }
  router.push({ name: 'Calendar' }).catch(() => {})
  if(lead.value.patient) {
    store.commit('setPatientFromCall', copyObject(lead.value.patient))
  }
  store.commit('setLeadForRecord', copyObject(lead.value))
  bvModal.hide('lead-show-modal')
}

const listen = async () => {
  if(recordLink.value) {
    window.open(recordLink.value, '_blank')
    return
  }
  if(callVendor.value === 'binotel' && lead.value?.patient_comment?.variables?.id) {
    const res = await BinotelService.getCallRecording(lead.value.patient_comment.variables.id)
    if(res.data?.url) {
      window.open(res.data.url, '_blank')
    } else {
      noty.error(i18n.t('record_not_available'))
    }
  }
  if(callVendor.value === 'uis' && lead.value?.patient_comment?.id) {
    const res = await BinotelService.getUISCallRecording(lead.value?.patient_comment?.id)
    if(res.data?.url) {
      window.open(res.data.url, '_blank')
    } else {
      noty.error(i18n.t('record_not_available'))
    }
  }
}

const patientSelected = (p) => {
  if(!p || form.value.patient_id === p.id) return
  form.value.patient_id = p.id
  changed.value = true
}

const onHide = () => {
  responsibleEditing.value = false
  store.commit('cleanValidationErrors')
  changed.value = false
}

onBeforeUnmount(() => {
  onHide()
})

const finished = async() => {
  if(changed.value) await saveChange()
  bvModal.hide('lead-show-modal')
}

const titleEditingClick = () => {
  if(!titleEditing.value) {
    titleEditing.value = true
    return
  }
  if(titleEditing.value) {
    titleEditing.value = false
    form.value.fname = lead.value.fname
    form.value.lname = lead.value.lname
  }
}

const responsibleChanged = async () => {
  await nextTick()
  await saveChange()
  lead.value.multiple_responsible = copyObject(form.value.multiple_responsible)
}

const addRecallTask = () => {
  store.commit('setModule', 'crm')
  store.commit('setViewModel', 'task')
  store.commit('setSelectedModel', {
    lead_id: lead.value.id,
    task_type: 'call',
    title: i18n.t('recall_on_lead', { title: lead.value.title }),
    deadline: moment().add(1, 'days').format('YYYY-MM-DD HH:mm'),
    start: moment().add(1, 'days').format('YYYY-MM-DD HH:mm'),
    patient_id: lead.value.patient_id,
    patient: lead.value.patient,
    phone: lead.value.phone || '',
    importance: 'high',
    user: user.value,
    user_id: user.value.id
  })
  store.commit('setShowModal', true)
}

const addTask = () => {
  store.commit('setModule', 'crm')
  store.commit('setViewModel', 'task')
  store.commit('setSelectedModel', {
    lead_id: lead.value.id,
    patient_id: lead.value.patient_id,
    patient: lead.value.patient,
  })
  store.commit('setShowModal', true)
}

const deleteLead = async () => {
  if(!confirm(i18n.t('confirm_deletion'))) return
  await EloquentService.delete('lead', lead.value.id)
  store.commit('setLead', null)
  bvModal.hide('lead-show-modal')
  noty.info(i18n.t('success_message'))
  store.commit('incrementDataTableCounter')
}

watch(rightPaneOpened, async (val) => {
  if(val) {
    bvModal.hide('lead-show-modal')
  }
})

watch(counter, () => {
  parse()
})
</script>

<style scoped lang="scss">
.lead-status-btn {
  line-height: 30px;
}
@media screen and (max-width: 768px) {
  .lead-status-btn {
    line-height: 24px;
  }
  .modal-footer-buttons {
    .btn-themed {
      padding: 0 20px;
    }
  }
}
</style>
